import { useMemo } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ExternalLink,
  Eye,
  RefreshCw,
  Search,
  Trash2,
  XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';
import { BantQualification, BantSortKey, BantFilter } from '../../types';

interface BantPageProps {
  // State
  qualifications: BantQualification[];
  loading: boolean;
  selectedQualification: BantQualification | null;
  detailsOpen: boolean;
  setDetailsOpen: (open: boolean) => void;
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: (open: boolean) => void;
  deleting: boolean;
  search: string;
  setSearch: (search: string) => void;
  filter: BantFilter;
  setFilter: (filter: BantFilter) => void;
  sort: { column: BantSortKey; direction: 'asc' | 'desc' };
  toggleSort: (column: BantSortKey) => void;
  totalElements: number;

  // Actions
  fetchQualifications: () => Promise<{ success: boolean; error?: string }>;
  deleteQualification: (chatId: string) => Promise<{ success: boolean; error?: string }>;
  openDetails: (qualification: BantQualification) => void;
  openDeleteDialog: (qualification: BantQualification) => void;
}

export function BantPage({
  qualifications,
  loading,
  selectedQualification,
  detailsOpen,
  setDetailsOpen,
  deleteDialogOpen,
  setDeleteDialogOpen,
  deleting,
  search,
  setSearch,
  filter,
  setFilter,
  sort,
  toggleSort,
  totalElements,
  fetchQualifications,
  deleteQualification,
  openDetails,
  openDeleteDialog
}: BantPageProps) {
  // Stats
  const stats = useMemo(() => {
    const qualified = qualifications.filter((q) => q.qualificado === 'Sim').length;
    const notQualified = qualifications.length - qualified;
    const avgScore =
      qualifications.length > 0
        ? qualifications.reduce((sum, q) => sum + q.score, 0) / qualifications.length
        : 0;
    return { qualified, notQualified, avgScore };
  }, [qualifications]);

  const SortIcon = ({ column }: { column: BantSortKey }) => {
    if (sort.column !== column) {
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    }
    return sort.direction === 'asc' ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBantBadge = (value: string) => {
    if (value === 'Sim') {
      return (
        <Badge variant="approved" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Sim
        </Badge>
      );
    }
    return (
      <Badge variant="rejected" className="gap-1">
        <XCircle className="h-3 w-3" />
        Nao
      </Badge>
    );
  };

  const handleDelete = async () => {
    if (!selectedQualification) return;
    await deleteQualification(selectedQualification.chat.chatId);
  };

  return (
    <>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Qualificacao</span>
        <span>/</span>
        <span className="font-medium text-foreground">BANT</span>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle>Qualificacoes BANT</CardTitle>
            <CardDescription>
              Visualize e gerencie as qualificacoes BANT dos leads.
            </CardDescription>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, telefone..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="button" onClick={fetchQualifications} disabled={loading}>
              <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
              <span className="hidden md:inline">{loading ? 'Atualizando' : 'Recarregar'}</span>
              <span className="md:hidden">Atualizar</span>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Stats Summary */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm border-b pb-3">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Total:</span>
              <span className="font-semibold">{totalElements}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">Qualificados:</span>
              <span className="font-medium text-green-600">{stats.qualified}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-red-600">Nao Qualificados:</span>
              <span className="font-medium text-red-600">{stats.notQualified}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Score Medio:</span>
              <span className="font-medium">{stats.avgScore.toFixed(1)}</span>
            </div>
          </div>

          <Tabs value={filter} onValueChange={(value) => setFilter(value as BantFilter)}>
            <TabsList>
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="qualified">Qualificados</TabsTrigger>
              <TabsTrigger value="not_qualified">Nao Qualificados</TabsTrigger>
            </TabsList>

            <TabsContent value={filter}>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                  </div>
                ) : qualifications.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    Nenhuma qualificacao encontrada.
                  </p>
                ) : (
                  qualifications.map((qualification) => (
                    <Card key={qualification.id} className="shadow-sm overflow-hidden">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold break-words">{qualification.chat.nome}</div>
                            <div className="text-sm text-muted-foreground">
                              {qualification.chat.telefone}
                            </div>
                          </div>
                          <Badge
                            variant={qualification.qualificado === 'Sim' ? 'approved' : 'rejected'}
                            className="flex-shrink-0"
                          >
                            {qualification.qualificado === 'Sim' ? 'Qualificado' : 'Nao Qualificado'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div>
                            <div className="text-xs text-muted-foreground">B</div>
                            {getBantBadge(qualification.budget)}
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">A</div>
                            {getBantBadge(qualification.authority)}
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">N</div>
                            {getBantBadge(qualification.need)}
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">T</div>
                            {getBantBadge(qualification.timeline)}
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t">
                          <div>
                            <div className="text-xs text-muted-foreground">Score</div>
                            <div className="text-lg font-semibold">{qualification.score}/4</div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDetails(qualification)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => openDeleteDialog(qualification)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => toggleSort('nome')}
                      >
                        <div className="flex items-center">
                          Nome
                          <SortIcon column="nome" />
                        </div>
                      </TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead className="text-center">B</TableHead>
                      <TableHead className="text-center">A</TableHead>
                      <TableHead className="text-center">N</TableHead>
                      <TableHead className="text-center">T</TableHead>
                      <TableHead
                        className="text-center cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => toggleSort('score')}
                      >
                        <div className="flex items-center justify-center">
                          Score
                          <SortIcon column="score" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => toggleSort('qualificado')}
                      >
                        <div className="flex items-center">
                          Status
                          <SortIcon column="qualificado" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => toggleSort('createdAt')}
                      >
                        <div className="flex items-center">
                          Data
                          <SortIcon column="createdAt" />
                        </div>
                      </TableHead>
                      <TableHead className="text-right">Acoes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center">
                          <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : qualifications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-sm">
                          Nenhuma qualificacao encontrada.
                        </TableCell>
                      </TableRow>
                    ) : (
                      qualifications.map((qualification) => (
                        <TableRow key={qualification.id}>
                          <TableCell className="font-medium">
                            {qualification.chat.nome}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {qualification.chat.telefone}
                          </TableCell>
                          <TableCell className="text-center">
                            {getBantBadge(qualification.budget)}
                          </TableCell>
                          <TableCell className="text-center">
                            {getBantBadge(qualification.authority)}
                          </TableCell>
                          <TableCell className="text-center">
                            {getBantBadge(qualification.need)}
                          </TableCell>
                          <TableCell className="text-center">
                            {getBantBadge(qualification.timeline)}
                          </TableCell>
                          <TableCell className="text-center font-semibold">
                            {qualification.score}/4
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={qualification.qualificado === 'Sim' ? 'approved' : 'rejected'}
                            >
                              {qualification.qualificado === 'Sim' ? 'Qualificado' : 'Nao Qualif.'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(qualification.createdAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDetails(qualification)}
                                title="Ver detalhes"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(qualification)}
                                title="Excluir"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Qualificacao BANT</DialogTitle>
            <DialogDescription>
              {selectedQualification?.chat.nome} - {selectedQualification?.chat.telefone}
            </DialogDescription>
          </DialogHeader>

          {selectedQualification && (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Informacoes do Contato
                </h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Nome</Label>
                    <p className="text-sm font-medium">{selectedQualification.chat.nome}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Telefone</Label>
                    <p className="text-sm font-medium">{selectedQualification.chat.telefone}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Chat ID</Label>
                    <p className="text-sm font-medium">{selectedQualification.chat.chatId}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Cliente Novo</Label>
                    <p className="text-sm font-medium">{selectedQualification.clienteNovo}</p>
                  </div>
                  {selectedQualification.chat.url && (
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-muted-foreground">Link</Label>
                      <a
                        href={selectedQualification.chat.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                      >
                        Abrir no Pipe.run
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* BANT Criteria */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Criterios BANT
                </h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label className="text-xs text-muted-foreground">Budget (Orcamento)</Label>
                      <p className="text-sm font-medium">Tem orcamento disponivel?</p>
                    </div>
                    {getBantBadge(selectedQualification.budget)}
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label className="text-xs text-muted-foreground">Authority (Autoridade)</Label>
                      <p className="text-sm font-medium">E o decisor?</p>
                    </div>
                    {getBantBadge(selectedQualification.authority)}
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label className="text-xs text-muted-foreground">Need (Necessidade)</Label>
                      <p className="text-sm font-medium">Tem necessidade real?</p>
                    </div>
                    {getBantBadge(selectedQualification.need)}
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label className="text-xs text-muted-foreground">Timeline (Prazo)</Label>
                      <p className="text-sm font-medium">Tem prazo definido?</p>
                    </div>
                    {getBantBadge(selectedQualification.timeline)}
                  </div>
                </div>
              </div>

              {/* Score and Status */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Resultado
                </h4>
                <div className="flex flex-wrap gap-4">
                  <div className="p-4 border rounded-lg text-center min-w-[100px]">
                    <Label className="text-xs text-muted-foreground">Score</Label>
                    <p className="text-3xl font-bold">{selectedQualification.score}/4</p>
                  </div>
                  <div className="p-4 border rounded-lg text-center min-w-[150px]">
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <div className="mt-2">
                      <Badge
                        variant={selectedQualification.qualificado === 'Sim' ? 'approved' : 'rejected'}
                        className="text-base px-3 py-1"
                      >
                        {selectedQualification.qualificado === 'Sim' ? 'Qualificado' : 'Nao Qualificado'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Observations */}
              {selectedQualification.observacoes && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Observacoes
                  </h4>
                  <div className="p-3 border rounded-lg bg-muted/50">
                    <p className="text-sm whitespace-pre-wrap">{selectedQualification.observacoes}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Datas
                </h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Criado em</Label>
                    <p className="text-sm font-medium">{formatDate(selectedQualification.createdAt)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Atualizado em</Label>
                    <p className="text-sm font-medium">{formatDate(selectedQualification.updatedAt)}</p>
                  </div>
                  {selectedQualification.dataPrimeiraEntrada && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Primeira Entrada</Label>
                      <p className="text-sm font-medium">
                        {formatDate(selectedQualification.dataPrimeiraEntrada)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusao</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir a qualificacao BANT de{' '}
              <strong>{selectedQualification?.chat.nome}</strong>? Esta acao nao pode ser desfeita.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
